import { Locale } from '~/locale/types';
import { ApiResponse } from '~/services/api.service';

export interface Product {
    productId?: number;
    number?: string;
    validatedOn?: string;
    renewType?: RenewType;
    issueDate?: string;
    isStillValid?: boolean;
    subscribeRR?: boolean;
}

export interface ProductValidationError {
    type?: string;
    title?: string;
    status?: number;
    detail?: string;
    instance?: string;
    additionalProp1?: string;
    additionalProp2?: string;
    additionalProp3?: string;
}

export type DriverClass =
    | 'A'
    | 'B'
    | 'C'
    | 'D'
    | 'E'
    | 'F'
    | 'G'
    | 'G1'
    | 'G2'
    | 'M'
    | 'M with L condition'
    | 'M1'
    | 'M2'
    | 'M2 with L condition';

export type DriverLicenseStatus =
    | 'LICENSED'
    | 'EXTENDED'
    | 'SUSPENDED'
    | 'CANCELLED'
    | 'UNLICENSED'
    | `PENDING ${DriverClass}`;

export type PhotoCardStatus = 'EXPIRED' | 'CANCELLED';

export type LocalizedValueType = {
    en: string | string[];
    fr: string | string[];
};

export interface PhotoCard extends Product {
    ddNumber?: string;
    expiryDate?: string;
    postalCode?: string;
    status?: LocalizedValueType;
}

export interface DriverLicense extends Product {
    trilliumNumber?: string;
    expiryDate?: string;
    postalCode?: string;
    status?: LocalizedValueType;
}

export interface VehiclePermit extends Product {
    rin?: string;
    expiryDate?: string;
    postalCode?: string;
}

export interface AIPhotoCard {
    firstName: string;
    lastName: string;
    middleName: string;
    dateOfBirth: string;
    dateOfExpiration: string;
    documentNumber: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    province: string;
    postalCode: string;
}

export interface InfoDetailsOptional {
    firstName: string | null;
    lastName: string | null;
    middleName?: string;
}

export interface HealthCard extends Product {
    versionCode?: string;
    controlNumber?: string;
    postalCode?: string;
    dateOfBirth?: string;
    expiryDate?: string;
}

export interface LicensePlate extends Product {
    registrantIdentificationNumber?: string | null;
    permitNumber?: string;
    driverLicenseNumber?: string | null;
    driverLicenseProductId?: number;
    expiryDate?: string;
}

export interface Products {
    [index: string]: Product | Product[] | undefined;
    driverLicense?: DriverLicense;
    photoCard?: PhotoCard;
    healthCard?: HealthCard;
    licencePlates?: LicensePlate[];
}

export interface Address {
    addressLine1: string;
    addressLine2?: string | null;
    streetSuffix?: string;
    city: string;
    provinceCode: string;
    postalCode: string;
    countryCode: string;
}

export interface AddressInfoOptional {
    addressLine1: string;
    addressLine2?: string;
    city?: string;
    province?: string;
}

export interface PersonalDetails {
    firstName?: string;
    middleName?: string | null;
    lastName?: string;
    dateOfBirth?: string;
    gender?: string;
    knownGenderPronoun?: string | null;
    customGenderPronoun?: string | null;
    agreedToTermsOfUseOn?: string;
    nocVersion?: string;
    myAccountAgreedOn?: string;
    profilePhoneDetails?: {
        phoneNumber?: string;
        extension?: string;
        alternatePhoneNumber?: string;
        alternateExtension?: string;
    };
    languageCode?: Locale;
    profileTier?: string;
    isVerifiable?: boolean;
}

export interface ProfileAccount {
    publicSecureUniqueIdentifier: string;
    publicSecureEmail: string;
}

export default interface Profile {
    details?: PersonalDetails;
    address?: Address;
    products?: Products;
}

export type TIdType =
    | 'DriversLicense'
    | 'HealthCard'
    | 'RedWhiteHealthCard'
    | 'LicensePlate'
    | 'VehiclePermit'
    | 'PhotoCard';

export type RenewType = 'Online' | 'InPerson';

export type NocResponseType = {
    driverLicense?: ApiResponse<DriverLicense>;
    healthCard?: ApiResponse<HealthCard>;
    licencePlates?: ApiResponse<LicensePlate[]>;
    photoCard?: ApiResponse<PhotoCard>;
};

export type OriginalCard = {
    driverLicense?: string;
    healthCard?: string;
    licencePlates?: string[];
    photoCard?: string;
};

export interface ProductDetailsFieldLabels {
    postalCode?: string;
}

export interface ChcUserInfo {
    firstName?: string;
    lastName?: string;
    middleName?: string;
    email?: string;
    userId?: string | null;
    lang: 'fr' | 'en';
    address?: Address;
    healthCard?: HealthCard;
    driverLicense?: DriverLicense;
}

export interface UserInfo {
    firstName?: string;
    lastName?: string;
    middleName?: string;
    email?: string;
    userId?: string | null;
    lang: 'fr' | 'en';
    address?: Address;
    healthCard?: HealthCard;
    driverLicense?: DriverLicense;
    token?: string;
}

export interface LicencePlateInquiryRequest {
    languageCode: string;
    licensePlate: {
        number: string;
        permitNumber: string;
    };
}

interface LicencePlateRenewalItemCost {
    unitCost: number;
    taxCode: string;
    taxRate: number;
    taxAmount: number;
    totalCost: number;
}

export type LicencePlateStatus =
    | 'NOT_RENEWABLE'
    | 'INVALID_INSURANCE'
    | 'RENEW_NOW'
    | 'PAY_DRIVER_FINE'
    | 'PAY_AND_RENEW'
    | 'RENEW_IN_PERSON';

export interface LicencePlateInquiryResponse {
    success: boolean;
    severity: string;
    messageCode: string;
    messageCodeDescription: string;
    renewalToken: string;
    transactionId: string;
    items: {
        item: {
            plateRenewal: {
                productCode: string;
                itemStatus: {
                    success: boolean;
                    responseMessage: {
                        messageCode: string;
                        severity: string;
                        logMessage: string;
                        presentationMessage: string;
                    };
                };
                licensePlateNumber: string;
                permitNumber: string;
                termInfo: {
                    itemCost: {
                        totalItemCost: LicencePlateRenewalItemCost;
                        productFee: LicencePlateRenewalItemCost;
                        additionalFees: {
                            unitCost: number;
                            taxCode: string;
                            taxRate: number;
                            taxAmount: number;
                            totalCost: number;
                            feeType: string;
                        }[];
                        denials: {
                            denialType: string;
                            denialTypeTotalAmount: number;
                            denialDetails: {
                                denialReferenceNumber: string;
                                courtNumber: string;
                                denialDescription: string;
                                denialFee: number;
                                denialPenaltyFee: number;
                                adminFee: number;
                            }[];
                        }[];
                    };
                    renewalTerm: number;
                    nextValtagExpiryDate: string;
                }[];
                gracePeriodExpiryDate: string;
                insuranceInfoRequired: boolean;
                odometerReadingRequired: boolean;
                driveCleanRequired: boolean;
                vehicleInfo: {
                    modelYear: string;
                    make: string;
                    model: string;
                    colour: string;
                };
            };
        };
    };
}

export const USER_PROFIL_KEY: string = 'userProfile';
